import React from 'react';
import { ArrowRight, CheckCircle, Globe } from 'lucide-react';
import { motion } from 'framer-motion';

interface HeroProps {
    onNavigate: (page: 'home' | 'blog' | 'contact' | 'resources' | 'products', sectionId?: string) => void;
}

const FloatingCapsule = ({ className, delay = 0, duration = 6, rotate = 0 }: { className?: string, delay?: number, duration?: number, rotate?: number }) => (
  <motion.div
    className={`absolute ${className}`}
    initial={{ y: 0, rotate: rotate }}
    animate={{ 
      y: [0, -20, 0],
      rotate: [rotate, rotate + 5, rotate - 5, rotate]
    }}
    transition={{ 
      duration: duration,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    {/* Capsule Shape */}
    <div className="w-8 h-24 md:w-12 md:h-36 rounded-full border-2 border-orange-100/50 bg-gradient-to-b from-white/40 to-white/10 backdrop-blur-sm shadow-lg flex flex-col overflow-hidden">
      <div className="h-1/2 w-full bg-orange-500/10 border-b border-orange-100/30"></div>
      <div className="h-1/2 w-full bg-white/20"></div>
    </div>
  </motion.div>
);

const FloatingTablet = ({ className, delay = 0, duration = 7 }: { className?: string, delay?: number, duration?: number }) => (
  <motion.div
    className={`absolute ${className}`}
    initial={{ y: 0, rotate: 0 }}
    animate={{ 
      y: [0, 15, 0],
      rotate: [0, 10, -10, 0]
    }}
    transition={{ 
      duration: duration,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    {/* Tablet Shape */}
    <div className="w-16 h-16 md:w-24 md:h-24 rounded-full border border-white/40 bg-gradient-to-br from-orange-50/80 to-white/20 backdrop-blur-md shadow-xl flex items-center justify-center">
      <div className="w-3/4 h-[1px] bg-black/5"></div>
    </div>
  </motion.div>
);

const FloatingSoftgel = ({ className, delay = 0, duration = 8 }: { className?: string, delay?: number, duration?: number }) => (
  <motion.div
    className={`absolute ${className}`}
    initial={{ y: 0, rotate: 45 }}
    animate={{ 
      y: [0, -15, 0],
      rotate: [45, 50, 40, 45]
    }}
    transition={{ 
      duration: duration,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    {/* Softgel Shape */}
    <div className="w-10 h-16 md:w-14 md:h-20 rounded-[2rem] bg-gradient-to-br from-orange-400/30 to-orange-600/10 backdrop-blur-sm border border-orange-200/30 shadow-lg rotate-45">
       <div className="absolute top-2 right-2 w-3 h-6 bg-white/30 rounded-full blur-[2px]"></div>
    </div>
  </motion.div>
);

const FloatingBottle = ({ className, delay = 0, duration = 9 }: { className?: string, delay?: number, duration?: number }) => (
  <motion.div
    className={`absolute ${className}`}
    initial={{ y: 0, rotate: -10 }}
    animate={{ 
      y: [0, -25, 0],
      rotate: [-10, -5, -15, -10]
    }}
    transition={{ 
      duration: duration,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    <div className="relative flex flex-col items-center drop-shadow-2xl">
      {/* Cap */}
      <div className="w-20 h-8 md:w-28 md:h-12 bg-gray-900 rounded-sm shadow-lg z-20 relative border-b border-gray-800 mx-auto">
        {/* Ribs on cap */}
        <div className="absolute inset-0 flex justify-around opacity-20">
            {[...Array(8)].map((_, i) => (
                <div key={i} className="w-[1px] h-full bg-gray-600"></div>
            ))}
        </div>
      </div>

      {/* Bottle Body Structure */}
      <div className="relative -mt-1 z-10 flex flex-col items-center">
          {/* Neck */}
          <div className="w-16 h-2 md:w-24 md:h-3 bg-gray-200 border-x border-gray-300"></div>
          
          {/* Shoulder */}
          <div className="w-22 h-4 md:w-32 md:h-6 bg-gray-100 rounded-t-3xl border-x border-t border-gray-200"></div>

          {/* Main Body */}
          <div className="relative w-24 h-32 md:w-36 md:h-48 bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-b-xl shadow-inner flex flex-col items-center overflow-hidden border-x border-b border-gray-200 ring-1 ring-black/5 -mt-[1px]">
            
            {/* Highlight/Reflection - Left */}
            <div className="absolute top-0 left-3 w-6 h-full bg-gradient-to-r from-white to-transparent opacity-60 z-20 pointer-events-none"></div>
            
            {/* Label */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-20 md:h-32 bg-white shadow-sm flex items-center justify-center z-10 border-y border-orange-100">
              <div className="text-center w-full px-3">
                <div className="text-xs md:text-lg font-black text-gray-900 leading-tight uppercase tracking-tight mt-1">Your Brand</div>
                <div className="text-xs md:text-lg font-black text-gray-900 leading-tight uppercase tracking-tight">Name</div>
                <div className="mt-2 w-full h-[1px] bg-orange-200 mx-auto"></div>
                <div className="mt-2 text-[8px] md:text-[10px] text-gray-700 font-medium">Dietary Supplement</div>
                <div className="mt-0.5 text-[6px] md:text-[8px] text-gray-600">60 Capsules</div>
                <div className="flex justify-center gap-1.5 mt-2">
                     <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                     <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                     <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>
  </motion.div>
);

const Hero: React.FC<HeroProps> = ({ onNavigate }) => {
  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 lg:pt-48 lg:pb-40 overflow-hidden bg-[#FAFAFA]">
      
      {/* Background Gradients */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-orange-200/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-orange-100/30 rounded-full blur-[100px]" />
      </div>

      {/* Animated Elements */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        {/* Top Left - Tablet */}
        <FloatingTablet className="top-[15%] left-[5%] md:left-[10%] opacity-60" delay={0} duration={6} />
        
        {/* Top Right - Capsule */}
        <FloatingCapsule className="top-[15%] right-[5%] md:right-[12%] opacity-80" delay={1} duration={7} rotate={15} />
        
        {/* Bottom Left - Softgel */}
        <FloatingSoftgel className="bottom-[20%] left-[8%] md:left-[15%] opacity-50" delay={2} duration={8} />
        
        {/* Bottom Right - Tablet (Small) */}
        <FloatingTablet className="bottom-[15%] right-[10%] md:right-[20%] scale-75 opacity-40" delay={1.5} duration={9} />
        
        {/* Center-ish Background - Blurred Capsule */}
        <FloatingCapsule className="top-[55%] left-[90%] scale-150 blur-[2px] opacity-20" delay={3} duration={10} rotate={-10} />

        {/* Floating Bottle with Brand Name */}
        <FloatingBottle className="top-[35%] right-[5%] opacity-90 z-10 hidden xl:block" delay={0.5} duration={9} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="lg:w-4/5 mx-auto text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-white/80 backdrop-blur-sm text-orange-800 font-semibold text-xs md:text-sm mb-8 border border-orange-100 shadow-sm"
          >
            <Globe className="w-3 h-3 md:w-4 md:h-4 mr-2" />
            Serving Brands in India & 15+ Countries
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-black tracking-tight leading-[1.1] mb-6 md:mb-8"
          >
            Custom Supplement Manufacturing That <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-700">Actually Delivers.</span>
              <span className="absolute bottom-2 left-0 w-full h-3 bg-orange-100/50 -z-10 transform -skew-x-6"></span>
            </span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-4 max-w-3xl mx-auto text-lg md:text-xl text-gray-600 mb-8 px-2 leading-relaxed"
          >
            Whether you're launching your first product or scaling an existing brand, BuildMySupplement delivers <span className="font-medium text-gray-900">GMP-certified manufacturing</span>, complete FSSAI compliance, and reliability you can count on.
          </motion.p>

          {/* Trust Badges */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-x-4 gap-y-2 md:gap-8 text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider mb-10"
          >
            {[
              "Global Shipping",
              "GMP & ISO 22000",
              "Flexible MOQs",
              "FSSAI Compliant"
            ].map((badge, index) => (
              <div key={index} className="flex items-center bg-white/50 border border-gray-100/50 backdrop-blur-sm px-3 py-1 rounded-lg md:bg-transparent md:border-0 md:p-0">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                {badge}
              </div>
            ))}
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row justify-center gap-4 mb-12 px-4 sm:px-0"
          >
            <button
              onClick={() => onNavigate('contact')}
              className="group relative inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-bold rounded-xl text-white bg-black hover:bg-gray-800 transition-all shadow-xl hover:shadow-orange-500/20 w-full sm:w-auto transform hover:-translate-y-1"
            >
              Launch Your Brand
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => onNavigate('contact')}
              className="inline-flex items-center justify-center px-8 py-4 border border-gray-200 text-lg font-bold rounded-xl text-gray-900 bg-white hover:border-black hover:text-black transition-all w-full sm:w-auto shadow-sm hover:shadow-md"
            >
              Scale Your Brand
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;